import Divider from '@mui/material/Divider';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  getClientContact,
  saveClientContact,
  ClientContactDTO,
  IUserDTO,
  ClientOwnerDTO,
  updateClientContact,
} from 'wisestep-api';

import { useUserContext } from '../../../context';
import { ClintPageBreadCrumb } from '../../../features/clientDetails/helper/clientDetailsHelper';
import { fetchAndSetAssignedUsers } from '../../../features/users/helper/hooks';
import getEncodedQueryParams from '../../../utils/getEncodedQueryParams';
import { failed, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { defaultClientContactDTO } from '../helper/ClientListingHelper';

import ClientContactForm from './clientContactComponents/ClientContactForm';
import ClientContactList from './clientContactComponents/ClientContactList';

interface ClientContactsProps {
  parentId?: string;
  clientId: string;
  showSavedData?: boolean;
  clientContactsReceived?: ClientContactDTO[];
  clientContactForEdit?: ClientContactDTO;
  setFormWizardCurrentStep?: (indices: number) => void;
  setFormWizardCurrentChildStep?: (indices: number) => void;
  closeOnUpdate?: () => void;
  clientOwner?: ClientOwnerDTO | null;
  clientName?: string | null;
  isClientContactList?: boolean;
}

const ClientContacts = ({
  clientId,
  showSavedData = true,
  clientContactForEdit,
  closeOnUpdate,
  setFormWizardCurrentStep,
  setFormWizardCurrentChildStep,
  clientContactsReceived,
  clientOwner,
  clientName,
  isClientContactList,
}: ClientContactsProps) => {
  const [clientContacts, setClientContacts] = useState<ClientContactDTO[]>(
    clientContactsReceived ?? []
  );
  const [param] = useSearchParams();
  const isChild: boolean = param.get('isChild') === 'false';
  const parentName: string = param.get('parentName') ?? '';
  const [clientContact, setClientContact] = useState<ClientContactDTO>(
    clientContactForEdit ?? defaultClientContactDTO(clientId)
  );
  const navigate = useNavigate();
  const setNotification = useNotificationStore((state) => state.setNotification);
  const loggedInUser = useUserContext().userDTO as IUserDTO;

  const [showExistingRecords, setShowExistingRecords] = useState<boolean>(showSavedData);

  useEffect(() => {
    getClientContacts(clientId);
  }, [clientId]);

  const getClientContacts = async (clientId) => {
    if (clientId) {
      const response = await getClientContact(clientId);
      if (response?.data?.data) {
        setClientContacts(response.data.data);
      }
    }
  };

  const handleCancel = () => handleEdit(defaultClientContactDTO(clientId));

  const handleEdit = (clientContact: ClientContactDTO) => setClientContact(clientContact);

  const onSubmit = async (data, isClientContactShareable) => {
    let response;
    try {
      const allowedRecruiters = isClientContactShareable
        ? await fetchAndSetAssignedUsers(loggedInUser.email ?? '')
        : [];
      data.allowedRecruiters = allowedRecruiters;
      response = data.id ? await updateClientContact(data) : await saveClientContact(data);
      if (closeOnUpdate) closeOnUpdate();
      else {
        if (showSavedData) {
          getClientContacts(clientId);
        } else setClientContacts([response.data.data]);

        setShowExistingRecords(true);
        handleEdit(defaultClientContactDTO(clientId));
      }
      setNotification(success('Client Contact has been saved successfully !!'));
    } catch {
      setNotification(failed('Something went wrong, Please try again !!'));
    }
    return response.data.data.id;
  };

  const onContinue = () => {
    closeOnUpdate
      ? closeOnUpdate()
      : navigate(
          `/client-details?isChild=${!isChild}&name=${
            parentName ?? ''
          }&clientId=${clientId}&breadCrumbList=${getEncodedQueryParams(
            ClintPageBreadCrumb['CLIENTS']
          )}`
        );
  };

  const onSkip = () =>
    navigate(
      `/client-details?isChild=${!isChild}&name=${
        parentName ?? ''
      }&clientId=${clientId}&breadCrumbList=${getEncodedQueryParams(
        ClintPageBreadCrumb['CLIENTS']
      )}`
    );
  const onBack = () => {
    setFormWizardCurrentStep && setFormWizardCurrentStep(0);
    setFormWizardCurrentChildStep && setFormWizardCurrentChildStep(3);
  };

  return (
    <>
      {showExistingRecords && (
        <>
          <ClientContactList
            clientContacts={clientContacts}
            handleCancel={handleCancel}
            handleEdit={handleEdit}
            clientContact={clientContact}
            refresh={() => getClientContacts(clientId)}
          />
          <Divider />
        </>
      )}
      <ClientContactForm
        clientId={clientId}
        onSubmit={onSubmit}
        onBack={onBack}
        onSkip={onSkip}
        onContinue={onContinue}
        closeOnUpdate={closeOnUpdate}
        clientContact={clientContact}
        saveButtonLabel={clientContact?.id ? 'Update' : closeOnUpdate ? 'Save' : 'Add More'}
        clientOwner={clientOwner}
        clientName={clientName}
        isClientContactList={isClientContactList}
      />
    </>
  );
};
export default ClientContacts;
